/**
 * Utility functions for Trakr Analytics Script
 */

import { ValidationResult, WebVitals, URLComponents } from './types';

/**
 * Debounce function to limit the rate of function calls
 */
export function debounce<T extends (...args: any[]) => any>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: NodeJS.Timeout | null = null;
  
  return function executedFunction(...args: Parameters<T>) {
    const later = () => {
      timeout = null;
      func(...args);
    };
    
    if (timeout !== null) {
      clearTimeout(timeout);
    }
    timeout = setTimeout(later, wait);
  };
}

/**
 * Generate UUID v4
 */
export function generateUUID(): string {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
}

/**
 * Cookie helper functions with error handling
 */
export function getCookie(name: string): string | null {
  try {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) {
      return parts.pop()?.split(';').shift() || null;
    }
    return null;
  } catch (error) {
    // Handle cases where document.cookie access fails (e.g., disabled cookies)
    return null;
  }
}

export function setCookie(name: string, value: string, days: number = 365): void {
  try {
    const expires = new Date(Date.now() + days * 24 * 60 * 60 * 1000).toUTCString();
    document.cookie = `${name}=${value}; expires=${expires}; path=/; SameSite=Lax`;
  } catch (error) {
    // Handle cases where document.cookie setting fails (e.g., disabled cookies)
    // Silently fail to avoid breaking the tracking
  }
}

/**
 * Validate domain against current location
 */
export function validateDomain(allowedDomain: string): ValidationResult {
  const currentDomain = window.location.hostname;
  
  // Remove www. prefix for comparison
  const normalizedCurrent = currentDomain.replace(/^www\./, '');
  const normalizedAllowed = allowedDomain.replace(/^www\./, '');
  
  if (normalizedCurrent === normalizedAllowed) {
    return { isValid: true };
  }
  
  return {
    isValid: false,
    error: `Domain mismatch: expected ${allowedDomain}, got ${currentDomain}`
  };
}

/**
 * Check if current environment should disable tracking
 */
export function shouldDisableTracking(debugMode: boolean = false): { disabled: boolean; reason?: string } {
  // Disable on localhost or file protocol
  if (/^localhost$|^127(\.[0-9]+){0,2}\.[0-9]+$|^\[::\1?\]$/.test(window.location.hostname) || 
      window.location.protocol === 'file:') {
    return { disabled: true, reason: 'localhost or file protocol' };
  }
  
  // Disable inside iframes unless debug mode is enabled
  if (window !== window.parent && !debugMode) {
    return { disabled: true, reason: 'inside iframe' };
  }
  
  return { disabled: false };
}

/**
 * Check if URL is external link
 */
export function isExternalLink(url: string): boolean {
  try {
    const link = new URL(url, window.location.href);
    return link.hostname !== window.location.hostname;
  } catch {
    return false;
  }
}

/**
 * Create regex pattern from glob-like pattern
 */
export function createPatternRegex(pattern: string): RegExp {
  const doubleAsteriskToken = "__DOUBLE_ASTERISK_TOKEN__";
  const singleAsteriskToken = "__SINGLE_ASTERISK_TOKEN__";
  
  let regexPattern = pattern
    .replace(/\*\*/g, doubleAsteriskToken)
    .replace(/\*/g, singleAsteriskToken)
    .replace(/[.+?^${}()|[\]\\]/g, '\\$&')
    .replace(new RegExp(doubleAsteriskToken, 'g'), '.*')
    .replace(new RegExp(singleAsteriskToken, 'g'), '[^/]*');
  
  return new RegExp(`^${regexPattern}$`);
}

/**
 * Check if URL matches any skip patterns
 */
export function shouldSkipUrl(url: string, skipPatterns: string[]): boolean {
  if (skipPatterns.length === 0) return false;
  
  return skipPatterns.some(pattern => {
    try {
      const regex = createPatternRegex(pattern);
      return regex.test(url);
    } catch {
      return false;
    }
  });
}

/**
 * Get visitor ID from cookie or generate new one
 */
export function getVisitorId(): string {
  const cookieName = '_traktiks_visitor_id';
  let visitorId = getCookie(cookieName);
  
  if (!visitorId) {
    visitorId = generateUUID();
    setCookie(cookieName, visitorId, 365); // 1 year
  }
  
  return visitorId;
}

/**
 * Get session ID from cookie or generate new one
 */
export function getSessionId(): string {
  const cookieName = '_traktiks_session_id';
  let sessionId = getCookie(cookieName);
  
  if (!sessionId) {
    sessionId = generateUUID();
    setCookie(cookieName, sessionId, 0.5); // 12 hours
  }
  
  return sessionId;
}

/**
 * Log debug messages if debug mode is enabled
 */
export function debugLog(message: string, data?: any, debugMode: boolean = false): void {
  if (!debugMode) return;
  if (data !== undefined && data !== null) {
    console.log(`[Traktiks] ${message}`, data);
  } else {
    console.log(`[Traktiks] ${message}`);
  }
}

/**
 * Log warnings
 */
export function logWarning(message: string): void {
  console.warn(`[Traktiks] ${message}`);
}

/**
 * Log errors
 */
export function logError(message: string, error?: any): void {
  if (error) {
    console.error(`[Traktiks Error] ${message}`, error);
  } else {
    console.error(`[Traktiks Error] ${message}`);
  }
}

// Web Vitals collection moved to webVitals.ts to use official web-vitals library
// This avoids deprecated Performance API warnings
export { collectWebVitals } from './webVitals';

/**
 * Parse URL into components
 */
export function parseURLComponents(href: string): URLComponents {
  try {
    const url = new URL(href);
    const urlParameters: Record<string, string> = {};
    
    // Parse query parameters
    url.searchParams.forEach((value, key) => {
      urlParameters[key] = value;
    });
    
    return {
      hostname: url.hostname,
      pathname: url.pathname,
      querystring: url.search.substring(1), // Remove the '?' prefix
      urlParameters
    };
  } catch (error) {
    debugLog('Error parsing URL components:', error);
    return {
      hostname: 'unknown',
      pathname: 'unknown',
      querystring: '',
      urlParameters: {}
    };
  }
}

/**
 * Get page title safely
 */
export function getPageTitle(): string {
  try {
    return document.title || 'Untitled';
  } catch (error) {
    debugLog('Error getting page title:', error);
    return 'Untitled';
  }
}

/**
 * Classify traffic channel based on referrer and URL parameters
 */
export function classifyTrafficChannel(referrer: string | null, urlParams: Record<string, string>): string {
  // Check for UTM parameters first
  if (urlParams.utm_source || urlParams.utm_medium || urlParams.utm_campaign) {
    const medium = urlParams.utm_medium?.toLowerCase();
    if (medium === 'email') return 'email';
    if (medium === 'social') return 'social';
    if (medium === 'cpc' || medium === 'ppc') return 'paid_search';
    if (medium === 'display') return 'display';
    if (medium === 'affiliate') return 'affiliate';
    return 'campaign';
  }
  
  // Check referrer
  if (!referrer) {
    return 'direct';
  }
  
  try {
    const referrerUrl = new URL(referrer);
    const referrerDomain = referrerUrl.hostname.toLowerCase();
    
    // Search engines
    if (referrerDomain.includes('google.') || referrerDomain.includes('bing.') || 
        referrerDomain.includes('yahoo.') || referrerDomain.includes('duckduckgo.') ||
        referrerDomain.includes('baidu.') || referrerDomain.includes('yandex.')) {
      return 'search';
    }
    
    // Social media
    if (referrerDomain.includes('facebook.') || referrerDomain.includes('twitter.') ||
        referrerDomain.includes('linkedin.') || referrerDomain.includes('instagram.') ||
        referrerDomain.includes('youtube.') || referrerDomain.includes('tiktok.') ||
        referrerDomain.includes('reddit.') || referrerDomain.includes('pinterest.')) {
      return 'social';
    }
    
    // Everything else is referral
    return 'referral';
  } catch (error) {
    debugLog('Error classifying traffic channel:', error);
    return 'unknown';
  }
}
